import React, { useEffect, useState, useMemo } from "react";
import { Sparkles, Clock } from "lucide-react";
import { endpoints } from "@/lib/api";

/**
 * SmartEntryRecent — a soft trail of the last 24h of N8 Smart-Entry
 * writes. Renders below the Tracking header as a chip strip so members
 * can *feel* what N8 just organized, without opening every tracker.
 *
 * Silent by design: no chip when there's nothing to show, no numbers
 * screaming, no "success" banners. Just a small witness.
 */

function relativeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const mins = Math.max(1, Math.round((Date.now() - t) / 60000));
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}

function itemLabel(it) {
  if (!it) return "";
  if (it.value_text) return it.value_text;
  if (it.status === "completed") {
    return String(it.tracker || "").replace(/_/g, " ");
  }
  if (it.value != null && it.unit) return `${it.value} ${it.unit} ${String(it.tracker || "").replace(/_/g, " ")}`.trim();
  if (it.value != null) return `${it.value} ${String(it.tracker || "").replace(/_/g, " ")}`.trim();
  return String(it.tracker || "").replace(/_/g, " ");
}

export default function SmartEntryRecent({ refreshKey = 0, testid = "smart-entry-recent" }) {
  const [entries, setEntries] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await endpoints.smartEntryHistory(24);
        if (!cancelled) setEntries(Array.isArray(data.entries) ? data.entries : []);
      } catch { if (!cancelled) setEntries([]); }
      finally { if (!cancelled) setLoaded(true); }
    })();
    return () => { cancelled = true; };
  }, [refreshKey]);

  // Flatten the last few Smart-Entry write batches into a single strip
  // of soft chips — no more than 6 chips so it doesn't shout.
  const chips = useMemo(() => {
    const out = [];
    for (const e of entries) {
      const ago = relativeAgo(e.created_at);
      for (const it of (e.items || [])) {
        out.push({ label: itemLabel(it), ago });
        if (out.length >= 6) return out;
      }
      if (out.length >= 6) break;
    }
    return out;
  }, [entries]);

  if (!loaded || chips.length === 0) return null;

  return (
    <div
      className="mt-4 flex flex-wrap items-center gap-2 text-xs text-[hsl(var(--muted-foreground))]"
      data-testid={testid}
    >
      <span className="inline-flex items-center gap-1.5 text-[hsl(var(--muted-foreground))]">
        <Sparkles size={12} className="text-[hsl(var(--terracotta))]" />
        <span>Just organized</span>
      </span>
      {chips.map((c, i) => (
        <span
          key={i}
          className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--muted))]/50 border border-[hsl(var(--border))] px-2.5 py-0.5"
          data-testid={`${testid}-chip-${i}`}
        >
          <span className="truncate max-w-[16rem]">{c.label}</span>
          <span className="text-[hsl(var(--muted-foreground))] opacity-70 inline-flex items-center gap-1">
            <Clock size={10} /> {c.ago}
          </span>
        </span>
      ))}
    </div>
  );
}
